/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Velocity Playbook System Schema and Types
 * 
 * Playbooks provide surrounding context libraries—the product knowledge, 
 * organizational standards, and strategic guidance that AI coding agents 
 * need to align their development output with product and organizational goals.
 * 
 * Key concepts:
 * - Organizational Playbooks: Cross-cutting standards stored in Velocity Repo
 * - Product Playbooks: Product-specific context stored in product repos
 * - Progressive Disclosure: TL;DR → Sections → Full content
 * - Index Playbook: Dynamic discovery of all available playbooks
 * 
 * @see product/docs/VELOCITY_PLAYBOOK_SYSTEM.md
 */

import { z } from 'zod';

// ============================================================================
// Playbook Scope and Status
// ============================================================================

export const PLAYBOOK_SCOPE = [
  'organizational', // Cross-cutting standards in Velocity Repo
  'product',        // Product-specific context in product repo
  'not_applicable', // Documents that shouldn't become playbooks (indexes, metadata, etc.)
] as const;

export type PlaybookScope = typeof PLAYBOOK_SCOPE[number];

export const PLAYBOOK_STATUS = [
  'draft',      // Work in progress, not ready for use
  'active',     // Ready for agent consumption
  'deprecated', // Outdated, being phased out
  'archived',   // No longer in use, kept for reference
] as const;

export type PlaybookStatus = typeof PLAYBOOK_STATUS[number];

// ============================================================================
// Playbook Domains (Categories)
// ============================================================================

export const ORGANIZATIONAL_PLAYBOOK_DOMAIN = [
  'strategy',    // Strategy & Vision
  'governance',  // Governance & Compliance
  'technical',   // Technical Standards
  'operational', // Operational Standards
  'systems',     // Enterprise Systems
  'other',       // Not applicable / unclassified (used for not_applicable scope)
] as const;

export type OrganizationalPlaybookDomain = typeof ORGANIZATIONAL_PLAYBOOK_DOMAIN[number];

export const PRODUCT_PLAYBOOK_DOMAIN = [
  'vision',       // Vision & Strategy
  'design',       // Design principles and UX
  'architecture', // Technical architecture
  'capabilities', // Features and capabilities
  'business',     // Business model
  'development',  // Development practices
] as const;

export type ProductPlaybookDomain = typeof PRODUCT_PLAYBOOK_DOMAIN[number];

export type PlaybookDomain = OrganizationalPlaybookDomain | ProductPlaybookDomain;

// ============================================================================
// Playbook Size Categories
// ============================================================================

export const PLAYBOOK_SIZE_CATEGORY = [
  'micro',    // 500-1,000 tokens - Single concept or procedure
  'standard', // 1,000-3,000 tokens - Typical playbook with examples
  'extended', // 3,000-5,000 tokens - Complex topics
  'maximum',  // 5,000-8,000 tokens - Comprehensive reference
] as const;

export type PlaybookSizeCategory = typeof PLAYBOOK_SIZE_CATEGORY[number];

// ============================================================================
// Playbook Metadata
// ============================================================================

/**
 * Metadata extracted from playbook frontmatter or header
 */
export interface PlaybookMetadata {
  /** Unique identifier for the playbook (e.g., "api_design", "user_archetypes") */
  id: string;
  
  /** Human-readable title */
  title: string;
  
  /** Domain category */
  domain: PlaybookDomain;
  
  /** Scope (organizational or product) */
  scope: PlaybookScope;
  
  /** Last updated timestamp (ISO 8601) */
  lastUpdated: string;
  
  /** Owner (team or individual) */
  owner: string;
  
  /** Playbook status */
  status: PlaybookStatus;
  
  /** Approximate token count */
  tokens: number;
  
  /** Size category based on tokens */
  sizeCategory: PlaybookSizeCategory;
  
  /** List of section names */
  sections: string[];
  
  /** Brief description (from Overview or first paragraph) */
  description?: string;
  
  /** Related playbook paths */
  relatedPlaybooks?: string[];
  
  /** Tags for search */
  tags?: string[];
  
  /** Product ID if product-scoped */
  productId?: string;
}

// ============================================================================
// Playbook Content
// ============================================================================

/**
 * A section within a playbook
 */
export interface PlaybookSection {
  /** Section name (from heading) */
  name: string;
  
  /** Section level (1-6) */
  level: number;
  
  /** Section content (markdown) */
  content: string;
  
  /** Approximate token count for section */
  tokens: number;
  
  /** Line number where section starts */
  startLine: number;
  
  /** Line number where section ends */
  endLine: number;
}

/**
 * Full playbook with parsed content
 */
export interface Playbook {
  /** Playbook metadata */
  metadata: PlaybookMetadata;
  
  /** File path relative to repo root */
  path: string;
  
  /** TL;DR content (required, 50-100 tokens) */
  tldr: string;
  
  /** Full raw content */
  rawContent: string;
  
  /** Parsed sections */
  sections: PlaybookSection[];
  
  /** File checksum for change detection */
  checksum: string;
  
  /** When this playbook was last loaded */
  loadedAt: string;
}

// ============================================================================
// Playbook Index
// ============================================================================

/**
 * Entry in the playbook index
 */
/**
 * Freshness status calculated from lastUpdated date
 * - fresh: Updated within 30 days
 * - stale: Updated 31-90 days ago
 * - outdated: Updated more than 90 days ago
 */
export type PlaybookFreshnessStatus = 'fresh' | 'stale' | 'outdated';

/**
 * Entry in the playbook index
 */
export interface PlaybookIndexEntry {
  /** Playbook ID */
  id: string;
  
  /** Display title */
  title: string;
  
  /** File path */
  path: string;
  
  /** Brief description */
  description: string;
  
  /** Domain */
  domain: PlaybookDomain;
  
  /** Scope */
  scope: PlaybookScope;
  
  /** Last updated date */
  lastUpdated: string;
  
  /** Freshness status calculated server-side from lastUpdated */
  freshness: PlaybookFreshnessStatus;
  
  /** Status */
  status: PlaybookStatus;
  
  /** Token count */
  tokens: number;
  
  /** Section names for quick reference */
  sections: string[];
  
  /** TL;DR content for quick orientation */
  tldr?: string;
  
  /** Product ID if product-scoped */
  productId?: string;
  
  /** Product title if product-scoped */
  productTitle?: string;
}

/**
 * Task type to playbook mapping for quick reference
 */
export interface TaskTypePlaybookMapping {
  /** Task type description */
  taskType: string;
  
  /** Recommended playbook IDs */
  recommendedPlaybooks: string[];
}

/**
 * Playbook index statistics
 */
export interface PlaybookIndexStats {
  /** Total playbook count */
  totalPlaybooks: number;
  
  /** Organizational playbook count */
  organizationalCount: number;
  
  /** Product playbook count */
  productCount: number;
  
  /** Most referenced playbook */
  mostReferenced?: {
    id: string;
    title: string;
    referenceCount: number;
  };
  
  /** Recently updated playbook */
  recentlyUpdated?: {
    id: string;
    title: string;
    lastUpdated: string;
  };
  
  /** Playbooks needing review (stale) */
  needsReview?: {
    id: string;
    title: string;
    daysSinceUpdate: number;
  }[];
}

/**
 * Source paths configuration for filesystem-first discovery
 * 
 * Stored in the index so PlaybookManager can scan the filesystem
 * directly without depending on the index for discovery.
 */
export interface PlaybookSourcePaths {
  /** Path to Velocity repo (for org playbooks) */
  velocityRepoPath?: string;
  
  /** Product configurations for product playbooks */
  products?: {
    id: string;
    title: string;
    docsPath: string;
  }[];
}

/**
 * Full playbook index
 */
export interface PlaybookIndex {
  /** Index version */
  version: string;
  
  /** Last updated timestamp */
  lastUpdated: string;
  
  /** Source paths for filesystem-first discovery */
  sourcePaths?: PlaybookSourcePaths;
  
  /** Index statistics */
  stats: PlaybookIndexStats;
  
  /** Task type to playbook mappings */
  taskTypeMappings: TaskTypePlaybookMapping[];
  
  /** Organizational playbooks by domain */
  organizational: Record<OrganizationalPlaybookDomain, PlaybookIndexEntry[]>;
  
  /** Product playbooks by product ID */
  products: Record<string, {
    productId: string;
    productTitle: string;
    playbooks: PlaybookIndexEntry[];
  }>;
}

// ============================================================================
// Playbook Search and Retrieval
// ============================================================================

/**
 * Search result for a playbook query
 */
export interface PlaybookSearchResult {
  /** Playbook entry */
  entry: PlaybookIndexEntry;
  
  /** Relevance score (0.0-1.0) */
  score: number;
  
  /** Matched terms */
  matchedTerms: string[];
  
  /** Match locations (section names where terms found) */
  matchLocations: string[];
}

/**
 * Options for playbook search
 */
export interface PlaybookSearchOptions {
  /** Query string */
  query: string;
  
  /** Limit results */
  limit?: number;
  
  /** Filter by scope */
  scope?: PlaybookScope;
  
  /** Filter by domain */
  domain?: PlaybookDomain;
  
  /** Filter by status */
  status?: PlaybookStatus;
  
  /** Filter by product */
  productId?: string;
  
  /** Include TL;DR in results */
  includeTldr?: boolean;
  
  /** Minimum relevance score */
  minScore?: number;
}

/**
 * Options for reading a playbook
 */
export interface PlaybookReadOptions {
  /** Playbook path or ID */
  playbookId: string;
  
  /** Read full playbook (false = TL;DR only) */
  full?: boolean;
  
  /** Specific sections to read */
  sections?: string[];
  
  /** Include metadata */
  includeMetadata?: boolean;
}

/**
 * Result of reading a playbook
 */
export interface PlaybookReadResult {
  /** Success flag */
  success: boolean;
  
  /** Playbook metadata */
  metadata?: PlaybookMetadata;
  
  /** Content (TL;DR, sections, or full) */
  content?: string;
  
  /** Sections if requested */
  sections?: PlaybookSection[];
  
  /** Token count of returned content */
  tokenCount?: number;
  
  /** Error message if failed */
  error?: string;
}

// ============================================================================
// Relevance Scoring
// ============================================================================

/**
 * Factors used for relevance scoring
 */
export interface RelevanceFactors {
  /** Keyword match score */
  keywordMatch: number;
  
  /** Domain match score */
  domainMatch: number;
  
  /** File path relevance */
  pathRelevance: number;
  
  /** Historical usage pattern */
  usagePattern: number;
  
  /** Recency boost */
  recency: number;
}

/**
 * Task context for relevance scoring
 */
export interface TaskContext {
  /** Task type (e.g., "api_development", "bug_fix") */
  taskType?: string;
  
  /** Keywords from task description */
  keywords: string[];
  
  /** File paths mentioned in task */
  filePaths: string[];
  
  /** Technical domains detected */
  domains: string[];
  
  /** Product ID if product-scoped */
  productId?: string;
}

// ============================================================================
// Playbook Freshness
// ============================================================================

/**
 * Freshness status for a playbook
 */
export interface PlaybookFreshness {
  /** Playbook ID */
  playbookId: string;
  
  /** Days since last update */
  daysSinceUpdate: number;
  
  /** Freshness status */
  status: 'fresh' | 'aging' | 'stale';
  
  /** Related code files that have changed */
  relatedCodeChanges?: {
    filePath: string;
    changeDate: string;
  }[];
  
  /** Broken references detected */
  brokenReferences?: string[];
  
  /** Staleness score (0.0-1.0, higher = more stale) */
  stalenessScore: number;
  
  /** Recommended action */
  recommendation?: 'none' | 'review' | 'update' | 'major_revision';
}

// ============================================================================
// Playbook Update
// ============================================================================

/**
 * Playbook update proposal
 */
export interface PlaybookUpdateProposal {
  /** Playbook ID */
  playbookId: string;
  
  /** Type of update */
  updateType: 'minor' | 'content' | 'major';
  
  /** Proposed changes */
  changes: {
    sectionName: string;
    originalContent: string;
    proposedContent: string;
    reason: string;
  }[];
  
  /** Source of update (manual, agent, code-change) */
  source: 'manual' | 'agent' | 'code_change' | 'scheduled_review';
  
  /** Whether auto-approve is recommended */
  autoApproveRecommended: boolean;
  
  /** Proposed by (user or agent ID) */
  proposedBy: string;
  
  /** Proposed at timestamp */
  proposedAt: string;
}

// ============================================================================
// Configuration
// ============================================================================

/**
 * Playbook system configuration
 */
export interface PlaybookSystemConfig {
  /** Freshness thresholds in days */
  freshnessThresholds: {
    fresh: number;  // Days under which playbook is fresh (default: 30)
    aging: number;  // Days until considered aging (default: 60)
    stale: number;  // Days until considered stale (default: 90)
  };
  
  /** Token budgets */
  tokenBudgets: {
    tldrMax: number;      // Max tokens for TL;DR (default: 100)
    sectionMax: number;   // Max tokens per section (default: 500)
    totalMax: number;     // Max total tokens to inject (default: 5000)
  };
  
  /** Relevance scoring weights */
  relevanceWeights: {
    keywordMatch: number;  // Weight for keyword matching (default: 0.3)
    domainMatch: number;   // Weight for domain matching (default: 0.25)
    pathRelevance: number; // Weight for path relevance (default: 0.2)
    usagePattern: number;  // Weight for usage patterns (default: 0.15)
    recency: number;       // Weight for recency (default: 0.1)
  };
  
  /** Minimum score for automatic inclusion */
  minimumRelevanceScore: number;
  
  /** Maximum playbooks to auto-include */
  maxAutoInclude: number;
  
  /** Index refresh interval in hours */
  indexRefreshIntervalHours: number;
}

// ============================================================================
// API Request/Response Types
// ============================================================================

/**
 * Request to search playbooks
 */
export interface SearchPlaybooksRequest {
  query: string;
  options?: Omit<PlaybookSearchOptions, 'query'>;
}

/**
 * Response for playbook search
 */
export interface SearchPlaybooksResponse {
  results: PlaybookSearchResult[];
  totalCount: number;
  query: string;
  searchedAt: string;
}

/**
 * Request to read a playbook
 */
export interface ReadPlaybookRequest {
  playbookId: string;
  options?: Omit<PlaybookReadOptions, 'playbookId'>;
}

/**
 * Request to get playbooks for a task
 */
export interface GetTaskPlaybooksRequest {
  taskContext: TaskContext;
  maxPlaybooks?: number;
  includeFullContent?: boolean;
}

/**
 * Response for task playbooks
 */
export interface GetTaskPlaybooksResponse {
  /** Recommended playbooks with relevance scores */
  playbooks: Array<{
    entry: PlaybookIndexEntry;
    score: number;
    content?: string;
  }>;
  
  /** Total tokens included */
  totalTokens: number;
  
  /** Task context used */
  taskContext: TaskContext;
}

// ============================================================================
// Zod Schemas for Validation
// ============================================================================

export const playbookMetadataSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  domain: z.string(),
  scope: z.enum(PLAYBOOK_SCOPE),
  lastUpdated: z.string(),
  owner: z.string(),
  status: z.enum(PLAYBOOK_STATUS),
  tokens: z.number().positive(),
  sizeCategory: z.enum(PLAYBOOK_SIZE_CATEGORY),
  sections: z.array(z.string()),
  description: z.string().optional(),
  relatedPlaybooks: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  productId: z.string().optional(),
});

export const playbookSectionSchema = z.object({
  name: z.string().min(1),
  level: z.number().min(1).max(6),
  content: z.string(),
  tokens: z.number().nonnegative(),
  startLine: z.number().nonnegative(),
  endLine: z.number().nonnegative(),
});

export const playbookIndexEntrySchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  path: z.string().min(1),
  description: z.string(),
  domain: z.string(),
  scope: z.enum(PLAYBOOK_SCOPE),
  lastUpdated: z.string(),
  status: z.enum(PLAYBOOK_STATUS),
  tokens: z.number().positive(),
  sections: z.array(z.string()),
  tldr: z.string().optional(),
  productId: z.string().optional(),
  productTitle: z.string().optional(),
});

export const playbookSearchOptionsSchema = z.object({
  query: z.string().min(1),
  limit: z.number().positive().optional(),
  scope: z.enum(PLAYBOOK_SCOPE).optional(),
  domain: z.string().optional(),
  status: z.enum(PLAYBOOK_STATUS).optional(),
  productId: z.string().optional(),
  includeTldr: z.boolean().optional(),
  minScore: z.number().min(0).max(1).optional(),
});

export const playbookReadOptionsSchema = z.object({
  playbookId: z.string().min(1),
  full: z.boolean().optional(),
  sections: z.array(z.string()).optional(),
  includeMetadata: z.boolean().optional(),
});

export const taskContextSchema = z.object({
  taskType: z.string().optional(),
  keywords: z.array(z.string()),
  filePaths: z.array(z.string()),
  domains: z.array(z.string()),
  productId: z.string().optional(),
});

export const searchPlaybooksRequestSchema = z.object({
  query: z.string().min(1),
  options: playbookSearchOptionsSchema.omit({ query: true }).optional(),
});

export const readPlaybookRequestSchema = z.object({
  playbookId: z.string().min(1),
  options: playbookReadOptionsSchema.omit({ playbookId: true }).optional(),
});

export const getTaskPlaybooksRequestSchema = z.object({
  taskContext: taskContextSchema,
  maxPlaybooks: z.number().positive().optional(),
  includeFullContent: z.boolean().optional(),
});

// ============================================================================
// Type Exports for Schema Inference
// ============================================================================

export type PlaybookMetadataInput = z.infer<typeof playbookMetadataSchema>;
export type PlaybookSectionInput = z.infer<typeof playbookSectionSchema>;
export type PlaybookIndexEntryInput = z.infer<typeof playbookIndexEntrySchema>;
export type PlaybookSearchOptionsInput = z.infer<typeof playbookSearchOptionsSchema>;
export type PlaybookReadOptionsInput = z.infer<typeof playbookReadOptionsSchema>;
export type TaskContextInput = z.infer<typeof taskContextSchema>;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get size category based on token count
 */
export function getPlaybookSizeCategory(tokens: number): PlaybookSizeCategory {
  if (tokens <= 1000) return 'micro';
  if (tokens <= 3000) return 'standard';
  if (tokens <= 5000) return 'extended';
  return 'maximum';
}

/**
 * Estimate token count for text (rough approximation)
 * Rule of thumb: 1 token ≈ 4 characters for English text
 */
export function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

/**
 * Generate playbook ID from title and optional domain
 * 
 * The domain is included in the ID to ensure uniqueness when playbooks
 * with the same title exist in different domains/directories.
 * 
 * @param title - Playbook title from the markdown heading
 * @param domain - Optional domain to include in the ID for uniqueness
 */
export function generatePlaybookId(title: string, domain?: string): string {
  const normalizedTitle = title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '_')
    .replace(/-+/g, '_')
    .substring(0, 50);
  
  // If domain is provided, prefix the ID to ensure uniqueness
  if (domain) {
    const normalizedDomain = domain
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
    return `${normalizedDomain}_${normalizedTitle}`.substring(0, 60);
  }
  
  return normalizedTitle;
}

/**
 * Calculate freshness status from a date string
 * 
 * Thresholds:
 * - fresh: Updated within 30 days
 * - stale: Updated 31-90 days ago  
 * - outdated: Updated more than 90 days ago
 * 
 * @param lastUpdated - ISO date string or Date object
 * @returns Freshness status ('fresh' | 'stale' | 'outdated')
 */
export function calculateFreshnessFromDate(lastUpdated: string | Date | undefined): PlaybookFreshnessStatus {
  if (!lastUpdated) {
    return 'stale';
  }
  
  const date = typeof lastUpdated === 'string' ? new Date(lastUpdated) : lastUpdated;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays <= 30) return 'fresh';
  if (diffDays <= 90) return 'stale';
  return 'outdated';
}

/**
 * Get freshness status based on days since update
 */
export function getFreshnessStatus(
  daysSinceUpdate: number,
  thresholds: { fresh: number; aging: number; stale: number }
): 'fresh' | 'aging' | 'stale' {
  if (daysSinceUpdate <= thresholds.fresh) return 'fresh';
  if (daysSinceUpdate <= thresholds.aging) return 'aging';
  return 'stale';
}

/**
 * Calculate staleness score (0.0-1.0)
 */
export function calculateStalenessScore(
  daysSinceUpdate: number,
  maxStaleDays: number = 180
): number {
  return Math.min(1.0, daysSinceUpdate / maxStaleDays);
}

/**
 * Default playbook system configuration
 */
export const DEFAULT_PLAYBOOK_CONFIG: PlaybookSystemConfig = {
  freshnessThresholds: {
    fresh: 30,
    aging: 60,
    stale: 90,
  },
  tokenBudgets: {
    tldrMax: 100,
    sectionMax: 500,
    totalMax: 5000,
  },
  relevanceWeights: {
    keywordMatch: 0.3,
    domainMatch: 0.25,
    pathRelevance: 0.2,
    usagePattern: 0.15,
    recency: 0.1,
  },
  minimumRelevanceScore: 0.4,
  maxAutoInclude: 5,
  indexRefreshIntervalHours: 24,
};

// ============================================================================
// AGENTS.md Generation
// ============================================================================

/**
 * Configuration for AGENTS.md generation
 * 
 * Products can customize AGENTS.md generation through these settings.
 * @see product/docs/VPS_PLAYBOOKS.md
 */
export interface AgentsMdSettings {
  /** Which playbooks to inline TL;DRs for (IDs or paths) */
  inlineTLDRs: string[];
  
  /** Additional static sections to include */
  additionalSections: {
    /** Section name/title */
    name: string;
    /** Section content (markdown) */
    content: string;
    /** Position: 'before_playbooks' | 'after_playbooks' | 'end' */
    position: 'before_playbooks' | 'after_playbooks' | 'end';
  }[];
  
  /** Which standard sections to skip */
  skipSections: ('project_overview' | 'custom_rules' | 'context_tools' | 'commands' | 'code_style' | 'architecture' | 'playbook_library' | 'skills_library' | 'mcp_servers' | 'testing' | 'security' | 'pr_guidelines')[];
  
  /** Custom header content */
  header?: string;
  
  /** Custom footer content */
  footer?: string;
  
  /** Playbook paths to include in library section */
  playbookPaths: {
    /** Development procedure playbooks */
    development: string[];
    /** Standards playbooks */
    standards: string[];
    /** Product context playbooks */
    productContext: string[];
  };
  
  /** Maximum TL;DRs to inline (default: 5) */
  maxInlineTLDRs?: number;
  
  /** Target token count for AGENTS.md (default: 1500) */
  targetTokens?: number;
}

/**
 * Default AGENTS.md settings
 */
export const DEFAULT_AGENTS_MD_SETTINGS: AgentsMdSettings = {
  inlineTLDRs: ['api_design', 'security', 'testing'],
  additionalSections: [],
  skipSections: ['mcp_servers'],
  playbookPaths: {
    development: [],
    standards: [],
    productContext: [],
  },
  maxInlineTLDRs: 5,
  targetTokens: 1500,
};

/**
 * Product metadata used for AGENTS.md generation
 */
export interface AgentsMdProductMetadata {
  /** Product name */
  name: string;
  
  /** Brief product description */
  description: string;
  
  /** Tech stack summary (optional - can be auto-detected from repository) */
  techStack?: string;
  
  /** Repository URL */
  repoUrl?: string;
  
  /** Local repository path for tech stack detection */
  repoPath?: string;
  
  /** Key directories description */
  directories?: {
    name: string;
    description: string;
  }[];
}

/**
 * Parsed package.json commands for AGENTS.md
 */
export interface AgentsMdCommands {
  /** Command name (e.g., "npm run dev") */
  command: string;
  
  /** Command description */
  description: string;
  
  /** Category (build, test, dev, lint, etc.) */
  category?: 'build' | 'test' | 'dev' | 'lint' | 'deploy' | 'other';
}

/**
 * Input for AGENTS.md generation
 */
export interface GenerateAgentsMdInput {
  /** Product ID */
  productId: string;
  
  /** Product metadata */
  metadata: AgentsMdProductMetadata;
  
  /** Commands from package.json */
  commands: AgentsMdCommands[];
  
  /** Generation settings */
  settings?: Partial<AgentsMdSettings>;
  
  /** Path to the product repo docs directory */
  docsPath?: string;
  
  /** Path to the organizational playbooks directory */
  orgPlaybooksPath?: string;
}

/**
 * Result of AGENTS.md generation
 */
export interface GenerateAgentsMdResult {
  /** Success flag */
  success: boolean;
  
  /** Generated AGENTS.md content */
  content?: string;
  
  /** Estimated token count */
  tokenCount?: number;
  
  /** Playbooks included */
  includedPlaybooks?: {
    id: string;
    title: string;
    path: string;
    inlined: boolean;
  }[];
  
  /** Error message if failed */
  error?: string;
}

/**
 * Trigger type for AGENTS.md regeneration
 */
export type AgentsMdTrigger = 
  | 'scheduled'       // Daily schedule
  | 'playbook_updated' // Playbook content changed
  | 'package_changed'  // package.json changed
  | 'manual';          // Manual request

/**
 * AGENTS.md generation event
 */
export interface AgentsMdGenerationEvent {
  /** Product ID */
  productId: string;
  
  /** Trigger type */
  trigger: AgentsMdTrigger;
  
  /** Event timestamp */
  timestamp: string;
  
  /** Details about the trigger */
  details?: {
    changedPlaybooks?: string[];
    changedFiles?: string[];
  };
}

// ============================================================================
// Playbook Update Tool Types (Phase 2 Task 4)
// ============================================================================

/**
 * Options for playbook update tool
 */
export interface PlaybookUpdateOptions {
  /** Playbook path to update */
  path: string;
  
  /** Section to update (or "new" for new section) */
  section?: string;
  
  /** New or updated content */
  content: string;
  
  /** Reason for the update */
  reason: string;
  
  /** Whether to auto-approve (minor updates only) */
  autoApprove?: boolean;
}

/**
 * Result of playbook update
 */
export interface PlaybookUpdateResult {
  /** Success flag */
  success: boolean;
  
  /** Update type determined */
  updateType?: 'minor' | 'content' | 'major';
  
  /** Whether update was auto-approved */
  autoApproved?: boolean;
  
  /** Path to draft file if not auto-approved */
  draftPath?: string;
  
  /** Commit SHA if auto-approved */
  commitSha?: string;
  
  /** Error message if failed */
  error?: string;
}

// ============================================================================
// AGENTS.md Zod Schemas
// ============================================================================

export const agentsMdSettingsSchema = z.object({
  inlineTLDRs: z.array(z.string()),
  additionalSections: z.array(z.object({
    name: z.string().min(1),
    content: z.string(),
    position: z.enum(['before_playbooks', 'after_playbooks', 'end']),
  })),
  skipSections: z.array(z.enum([
    'project_overview', 'context_tools', 'commands', 'code_style', 'architecture',
    'playbook_library', 'skills_library', 'mcp_servers', 'testing', 'security', 'pr_guidelines'
  ])),
  header: z.string().optional(),
  footer: z.string().optional(),
  playbookPaths: z.object({
    development: z.array(z.string()),
    standards: z.array(z.string()),
    productContext: z.array(z.string()),
  }),
  maxInlineTLDRs: z.number().positive().optional(),
  targetTokens: z.number().positive().optional(),
});

export const agentsMdProductMetadataSchema = z.object({
  name: z.string().min(1),
  description: z.string(),
  techStack: z.string().optional(),
  repoUrl: z.string().url().optional(),
  repoPath: z.string().optional(),
  directories: z.array(z.object({
    name: z.string().min(1),
    description: z.string(),
  })).optional(),
});

export const generateAgentsMdInputSchema = z.object({
  productId: z.string().min(1),
  metadata: agentsMdProductMetadataSchema,
  commands: z.array(z.object({
    command: z.string().min(1),
    description: z.string(),
    category: z.enum(['build', 'test', 'dev', 'lint', 'deploy', 'other']).optional(),
  })),
  settings: agentsMdSettingsSchema.partial().optional(),
  docsPath: z.string().optional(),
  orgPlaybooksPath: z.string().optional(),
});

export const playbookUpdateOptionsSchema = z.object({
  path: z.string().min(1),
  section: z.string().optional(),
  content: z.string().min(1),
  reason: z.string().min(1),
  autoApprove: z.boolean().optional(),
});

export type AgentsMdSettingsInput = z.infer<typeof agentsMdSettingsSchema>;
export type GenerateAgentsMdInputInput = z.infer<typeof generateAgentsMdInputSchema>;
export type PlaybookUpdateOptionsInput = z.infer<typeof playbookUpdateOptionsSchema>;
